import React, { useState, useEffect } from 'react';
import api, { formatMoney, toShamsi, AFGHAN_MONTHS, EXPENSE_CATEGORIES } from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [partners, setPartners] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const { isAdmin, user } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [partnerFilter, setPartnerFilter] = useState('');
  const [form, setForm] = useState({ amount: '', category: '', description: '', expense_date: now.toISOString().split('T')[0], partner_id: '' });
  const [msg, setMsg] = useState('');

  const load = async () => {
    const params = `?month=${month}&year=${year}${partnerFilter ? `&partner_id=${partnerFilter}` : ''}`;
    const { data } = await api.get(`/expenses${params}`);
    setExpenses(data);
  };

  useEffect(() => { load(); }, [month, year, partnerFilter]);
  useEffect(() => {
    if (isAdmin) api.get('/auth/users').then(r => setPartners(r.data.filter(u => u.is_partner)));
  }, [isAdmin]);

  const openAdd = () => {
    setEditing(null);
    setForm({ amount: '', category: '', description: '', expense_date: now.toISOString().split('T')[0], partner_id: '' });
    setModal(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({ amount: e.amount, category: e.category || '', description: e.description, expense_date: e.expense_date?.split('T')[0] || '', partner_id: e.partner_id });
    setModal(true);
  };

  const handleSave = async () => {
    try {
      if (editing) await api.put(`/expenses/${editing.id}`, form);
      else await api.post('/expenses', form);
      setModal(false); setMsg(''); load();
    } catch (err) { setMsg(err.response?.data?.message || 'خطا'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('این مصرف حذف شود؟')) return;
    await api.delete(`/expenses/${id}`); load();
  };

  const handleApprove = async (id) => {
    await api.put(`/expenses/${id}/approve`); load();
  };

  // گروه‌بندی بر اساس شریک
  const byPartner = expenses.reduce((acc, e) => {
    const key = e.partner_name;
    if (!acc[key]) acc[key] = { name: key, total: 0, items: [] };
    acc[key].total += Number(e.amount);
    acc[key].items.push(e);
    return acc;
  }, {});

  const grandTotal = expenses.reduce((s, e) => s + Number(e.amount), 0);
  const shamsiYear = year - 621;

  return (
    <div>
      <div className="page-header">
        <h1>💼 مصارف شخصی شرکا</h1>
        <button className="btn btn-primary" onClick={openAdd}>➕ ثبت مصرف</button>
      </div>

      {/* فیلترها */}
      <div className="card" style={{ marginBottom: 16, padding: '14px 20px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>ماه:</span>
          <select className="form-control" style={{ width: 130 }} value={month} onChange={e => setMonth(e.target.value)}>
            {AFGHAN_MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select className="form-control" style={{ width: 100 }} value={year} onChange={e => setYear(e.target.value)}>
            {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y - 621}</option>)}
          </select>
          {isAdmin && partners.length > 0 && (
            <>
              <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>شریک:</span>
              <select className="form-control" style={{ width: 150 }} value={partnerFilter} onChange={e => setPartnerFilter(e.target.value)}>
                <option value="">همه شرکا</option>
                {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </>
          )}
          <div style={{ marginRight: 'auto', fontWeight: 600, color: 'var(--danger)', fontSize: 15 }}>
            مجموع: {formatMoney(grandTotal)}
          </div>
        </div>
      </div>

      {/* خلاصه شرکا */}
      {isAdmin && Object.keys(byPartner).length > 0 && (
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          {Object.values(byPartner).map(p => (
            <div key={p.name} className="stat-card">
              <div className="stat-label">👤 {p.name}</div>
              <div className="stat-value" style={{ fontSize: 18, color: 'var(--accent)' }}>{formatMoney(p.total)}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{p.items.length} مورد در {AFGHAN_MONTHS[month - 1]}</div>
            </div>
          ))}
        </div>
      )}

      {/* جدول مصارف */}
      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {isAdmin && <th>شریک</th>}
                <th>تاریخ</th><th>دسته</th><th>توضیح</th><th>مقدار</th><th>وضعیت</th><th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map(e => (
                <tr key={e.id}>
                  {isAdmin && <td style={{ fontWeight: 600 }}>{e.partner_name}</td>}
                  <td style={{ fontSize: 12 }}>{toShamsi(e.expense_date)}</td>
                  <td><span className="badge badge-info">{e.category || '—'}</span></td>
                  <td style={{ maxWidth: 200 }}>{e.description}</td>
                  <td style={{ fontWeight: 700, color: 'var(--accent)' }}>{formatMoney(e.amount)}</td>
                  <td>
                    {e.approved_by
                      ? <span className="badge badge-success">✓ تایید</span>
                      : <span className="badge badge-warning">انتظار</span>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {isAdmin && !e.approved_by && (
                        <button className="btn btn-success btn-sm" onClick={() => handleApprove(e.id)}>✓</button>
                      )}
                      {isAdmin && <button className="btn btn-outline btn-sm" onClick={() => openEdit(e)}>✏️</button>}
                      {isAdmin && <button className="btn btn-danger btn-sm" onClick={() => handleDelete(e.id)}>🗑️</button>}
                    </div>
                  </td>
                </tr>
              ))}
              {expenses.length === 0 && <tr><td colSpan={isAdmin ? 7 : 6} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>مصرفی ثبت نشده</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{editing ? 'ویرایش مصرف' : 'ثبت مصرف جدید'}</span>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>
            {msg && <div className="alert alert-danger">{msg}</div>}
            {isAdmin && (
              <div className="form-group">
                <label className="form-label">شریک</label>
                <select className="form-control" value={form.partner_id} onChange={e => setForm({ ...form, partner_id: e.target.value })}>
                  <option value="">— خودم —</option>
                  {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">مقدار (افغانی) *</label>
                <input className="form-control" type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">تاریخ</label>
                <input className="form-control" type="date" value={form.expense_date} onChange={e => setForm({ ...form, expense_date: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">دسته‌بندی</label>
              <select className="form-control" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                <option value="">انتخاب کنید</option>
                {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">توضیح *</label>
              <textarea className="form-control" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="در کجا، به چه منظور..." />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setModal(false)}>انصراف</button>
              <button className="btn btn-primary" onClick={handleSave}>💾 ذخیره</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
